import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { customToast } from "../../../../components/notify";
import { INITIAL_GAME_FORM_DATA, INITIAL_GAMES_TOGGLE } from "../configs";
import { GAME_LANGUAGES, MAX_GAME_ITEMS, MAX_QUIZ_OPTIONS } from "../constants";
import { useGetGameSettingQuery, useUpdateGameSettingMutation } from "../services/gameApiSlice";
import { useGetKioskSettingQuery, useUpdateKioskSettingMutation } from "../services/kioskApiSlice";
import {
    GameLanguageCode,
    GameType,
    IApiFlipCardItem,
    IApiGameSettings,
    IApiGamesToggle,
    IApiLanguageContent,
    IApiPrizeItem,
    IApiPuzzleItem,
    IApiQuizItem,
    IGameSettingsFormData,
    IKioskSettingNative,
} from "../types";
import { gameSettingsValidationSchema } from "../validations";
import useBatchUploadFiles from "./useBatchUploadFiles";
import { useTabGuard } from "./useTabGuard";

// ============ Props Interface ============
interface UseGameSettingsProps {
    characterId: string;
}

// ============ Form Type with File Fields ============
// NonNullable<unknown> satisfies useForm's DeepPartial constraint (excludes null from index signature)
type GameFormWithFiles = IGameSettingsFormData & Record<string, NonNullable<unknown> | undefined>;

export const useGameSettings = ({ characterId }: UseGameSettingsProps) => {
    const { t } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState<GameLanguageCode>("zh-TW");

    // 追蹤是否為初始載入，避免儲存後 refetch 觸發 reset 導致閃爍
    const isInitialLoadRef = useRef(true);

    // ====================== API Queries ======================
    // Game Settings API (遊戲資料)
    const { data: gameSettingResponse, isLoading: isLoadingGameSetting } = useGetGameSettingQuery(
        { character_id: characterId },
        { skip: !characterId }
    );

    // Kiosk Settings API (開關狀態)
    const { data: kioskSettingResponse, isLoading: isLoadingKioskSetting } = useGetKioskSettingQuery(
        { character_id: characterId },
        { skip: !characterId }
    );

    // ====================== API Mutations ======================
    const [updateGameSetting, { isLoading: isUpdatingGameSetting }] = useUpdateGameSettingMutation();
    const [updateKioskSetting, { isLoading: isUpdatingKioskSetting }] = useUpdateKioskSettingMutation();
    const { uploadAllFiles, isUploading } = useBatchUploadFiles();

    // ====================== Form Setup ======================
    const methods = useForm<GameFormWithFiles>({
        mode: "onChange",
        resolver: yupResolver(gameSettingsValidationSchema),
        defaultValues: INITIAL_GAME_FORM_DATA as GameFormWithFiles,
    });

    const { handleSubmit, watch, setValue, reset } = methods;

    const watchedEnabled = watch("enabled");
    const watchedPuzzleEnabled = watch("games.puzzle.enabled");
    const watchedQuizEnabled = watch("games.quiz.enabled");
    const watchedFlipCardEnabled = watch("games.flipcard.enabled");
    const watchedPrizeEnabled = watch("prize.common.enabled");

    // ====================== Derived State ======================
    const isLoading = isLoadingGameSetting || isLoadingKioskSetting;
    const isUpdating = isUpdatingGameSetting || isUpdatingKioskSetting || isUploading;

    // Get current kiosk native settings (for merging on update)
    const currentKioskNative = useMemo(() => {
        return kioskSettingResponse?.data?.native;
    }, [kioskSettingResponse?.data?.native]);

    // ====================== Data Conversion ======================

    //  合併 kiosk_settings (開關) 和 game_settings (資料) 到表單資料 
    const convertApiToFormData = useCallback(
        (gameData: IApiGameSettings | null, gamesToggle: IApiGamesToggle): IGameSettingsFormData => {
            // 使用 kiosk_settings 的開關狀態
            const masterEnabled = gamesToggle.enabled;
            const puzzleEnabled = gamesToggle.permission.puzzle;
            const quizEnabled = gamesToggle.permission.quiz;
            const flipCardEnabled = gamesToggle.permission["flip-card"];
            const prizeEnabled = gamesToggle.permission.prize;

            // 如果沒有 game_settings 資料，使用預設值
            if (!gameData) {
                return {
                    ...INITIAL_GAME_FORM_DATA,
                    enabled: masterEnabled,
                    games: {
                        ...INITIAL_GAME_FORM_DATA.games,
                        puzzle: { ...INITIAL_GAME_FORM_DATA.games.puzzle, enabled: puzzleEnabled },
                        quiz: { ...INITIAL_GAME_FORM_DATA.games.quiz, enabled: quizEnabled },
                        flipcard: { ...INITIAL_GAME_FORM_DATA.games.flipcard, enabled: flipCardEnabled },
                    },
                    prize: {
                        ...INITIAL_GAME_FORM_DATA.prize,
                        common: { ...INITIAL_GAME_FORM_DATA.prize.common, enabled: prizeEnabled },
                    },
                };
            }

            return {
                enabled: masterEnabled,
                games: {
                    puzzle: {
                        ...gameData.games.puzzle,
                        enabled: puzzleEnabled, // 從 kiosk_settings 取得
                        bgmFile: null,
                        bgFile: null,
                    },
                    quiz: {
                        ...gameData.games.quiz,
                        enabled: quizEnabled, // 從 kiosk_settings 取得
                        bgmFile: null,
                        bgFile: null,
                    },
                    flipcard: {
                        ...gameData.games["flip-card"],
                        enabled: flipCardEnabled, // 從 kiosk_settings 取得
                        bgmFile: null,
                        bgFile: null,
                    },
                },
                prize: {
                    ...gameData.prize,
                    common: {
                        ...gameData.prize.common,
                        enabled: prizeEnabled, // 從 kiosk_settings 取得
                    },
                },
            };
        },
        []
    );

    //  將表單資料轉換為 game_settings API payload (不含 enabled)
    const convertFormToGamePayload = useCallback((formData: IGameSettingsFormData): IApiGameSettings => {
        return {
            games: {
                puzzle: {
                    enabled: formData.games.puzzle.enabled,
                    game_time: formData.games.puzzle.game_time,
                    puzzle_count: formData.games.puzzle.puzzle_count,
                    bgm: formData.games.puzzle.bgm,
                    bg: formData.games.puzzle.bg,
                    data: formData.games.puzzle.data,
                },
                quiz: {
                    enabled: formData.games.quiz.enabled,
                    total_per_round: formData.games.quiz.total_per_round,
                    pass_count: formData.games.quiz.pass_count,
                    bgm: formData.games.quiz.bgm,
                    bg: formData.games.quiz.bg,
                    data: formData.games.quiz.data,
                },
                "flip-card": {
                    enabled: formData.games.flipcard.enabled,
                    game_time: formData.games.flipcard.game_time,
                    pair_count: formData.games.flipcard.pair_count,
                    bgm: formData.games.flipcard.bgm,
                    bg: formData.games.flipcard.bg,
                    data: formData.games.flipcard.data,
                },
            },
            prize: formData.prize,
        };
    }, []);

    //  將表單資料轉換為 kiosk_settings 的 games toggle
    const convertFormToGamesToggle = useCallback((formData: IGameSettingsFormData): IApiGamesToggle => {
        return {
            enabled: formData.enabled,
            permission: {
                puzzle: formData.games.puzzle.enabled,
                quiz: formData.games.quiz.enabled,
                "flip-card": formData.games.flipcard.enabled,
                prize: formData.prize.common.enabled,
            },
        };
    }, []);

    // ====================== Toggle Handlers ======================
    const handleMasterToggle = useCallback(
        (enabled: boolean) => {
            setValue("enabled", enabled, { shouldDirty: true });
        },
        [setValue]
    );

    const handleGameToggle = useCallback(
        (gameType: GameType | "prize", enabled: boolean) => {
            if (gameType === "prize") {
                setValue("prize.common.enabled", enabled, { shouldDirty: true });
            } else {
                const formGameType = gameType === "flip-card" ? "flipcard" : gameType;
                setValue(`games.${formGameType}.enabled`, enabled, { shouldDirty: true });
            }
        },
        [setValue]
    );

    // ===================== Langauge Helpers ======================
    const getLanguageIndex = useCallback((lang: GameLanguageCode): number => {
        return GAME_LANGUAGES.findIndex((l) => l.code === lang);
    }, []);

    // ============ Item Count by Language ============
    const getItemCountsByLang = useCallback(
        (gameType: GameType | "prize"): Record<GameLanguageCode, number> => {
            const formValues = methods.getValues();
            const counts: Record<GameLanguageCode, number> = {
                "zh-TW": 0,
                "zh-CN": 0,
                "en-US": 0,
                "ja-JP": 0,
                "ko-KR": 0,
            };

            const formGameType = gameType === "flip-card" ? "flipcard" : gameType;

            let dataArray;
            if (gameType === "prize") {
                dataArray = formValues.prize.data;
            } else {
                dataArray = formValues.games[formGameType as "puzzle" | "quiz" | "flipcard"].data;
            }

            dataArray?.forEach((langContent) => {
                counts[langContent.lang] = langContent.content?.length || 0;
            });

            return counts;
        },
        [methods]
    );

    // ============ Item Factories ============
    const createPuzzleItem = useCallback(
        (): IApiPuzzleItem => ({
            id: uuidv4(),
            image_url: "",
            title: "",
            description: "",
        }),
        []
    );

    const createQuizItem = useCallback(
        (): IApiQuizItem => ({
            id: uuidv4(),
            title: "",
            image_url: "",
            options: Array.from({ length: MAX_QUIZ_OPTIONS }, () => ({
                text: "",
                image_url: "",
            })),
            correct_option_index: 0,
        }),
        []
    );

    const createFlipCardItem = useCallback(
        (): IApiFlipCardItem => ({
            id: uuidv4(),
            image_url: "",
            title: "",
            description: "",
        }),
        []
    );

    const createPrizeItem = useCallback(
        (): IApiPrizeItem => ({
            id: uuidv4(),
            title: "",
            type: "none",
            url: "",
        }),
        []
    );

    // ============ Add Item Handler ============
    const handleAddItem = useCallback(
        (gameType: GameType | "prize", lang: GameLanguageCode) => {
            const langIndex = getLanguageIndex(lang);
            if (langIndex === -1) return;

            // Normalize gameType for form path (flip-card -> flipcard)
            const formGameType = gameType === "flip-card" ? "flipcard" : gameType;

            const basePath = gameType === "prize" ? "prize.data" : `games.${formGameType}.data`;
            const fullPath = `${basePath}.${langIndex}.content`;
            const langObjectPath = `${basePath}.${langIndex}`;

            const dataArray = methods.getValues(basePath as never) as unknown as IApiLanguageContent<unknown>[] | undefined;

            let langObject = dataArray?.[langIndex];
            if (!langObject) {
                langObject = {
                    lang,
                    content: [],
                };
                // Set the language object first
                const updatedDataArray = [...(dataArray || [])];
                updatedDataArray[langIndex] = langObject;
                setValue(basePath as never, updatedDataArray as never, { shouldDirty: true });
            } else if (langObject.lang !== lang) {
                langObject.lang = lang;
                setValue(`${langObjectPath}.lang` as never, lang as never, { shouldDirty: true });
            }

            const currentData = (langObject?.content || []) as unknown[];

            if (currentData.length >= MAX_GAME_ITEMS) {
                customToast.error(t("APPWIDGET.GAME_MAX_ITEMS_REACHED") || `最多只能新增 ${MAX_GAME_ITEMS} 個項目`);
                return;
            }

            let newItem;
            switch (gameType) {
                case "puzzle":
                    newItem = createPuzzleItem();
                    break;
                case "quiz":
                    newItem = createQuizItem();
                    break;
                case "flip-card":
                    newItem = createFlipCardItem();
                    break;
                case "prize":
                    newItem = createPrizeItem();
                    break;
            }

            const newData = [...currentData, newItem];

            setValue(fullPath as never, newData as never, {
                shouldDirty: true,
            });
        },
        [getLanguageIndex, methods, setValue, t, createPuzzleItem, createQuizItem, createFlipCardItem, createPrizeItem]
    );

    // ============ Remove Item Handler ============
    const handleRemoveItem = useCallback(
        (gameType: GameType | "prize", lang: GameLanguageCode, itemIndex: number) => {
            const langIndex = getLanguageIndex(lang);
            if (langIndex === -1) return;

            const formGameType = gameType === "flip-card" ? "flipcard" : gameType;

            const basePath = gameType === "prize" ? "prize.data" : `games.${formGameType}.data`;
            const rawData = methods.getValues(`${basePath}.${langIndex}.content` as never) || [];
            const currentData = rawData as unknown as unknown[];

            const updatedData = currentData.filter((_: unknown, idx: number) => idx !== itemIndex);

            setValue(`${basePath}.${langIndex}.content` as never, updatedData as never, {
                shouldDirty: true,
            });
        },
        [getLanguageIndex, methods, setValue]
    );

    // ============ Submit Handler with Dual API Update & Rollback ============
    const onSubmit = useCallback(
        async (data: GameFormWithFiles) => {
            // 保存原始 kiosk_settings 用於 rollback
            const originalKioskNative = currentKioskNative;

            try {
                const dataWithUrls = await uploadAllFiles<GameFormWithFiles>(data);

                const gamePayload = convertFormToGamePayload(dataWithUrls as IGameSettingsFormData);
                const gamesToggle = convertFormToGamesToggle(dataWithUrls as IGameSettingsFormData);

                await updateGameSetting({
                    character_id: characterId,
                    payload: gamePayload,
                }).unwrap();

                try {
                    const updatedNative: IKioskSettingNative = {
                        ...originalKioskNative,
                        games: gamesToggle,
                    } as IKioskSettingNative;

                    await updateKioskSetting({
                        character_id: characterId,
                        native: updatedNative,
                    }).unwrap();
                } catch (kioskError) {
                    const rollbackPayload: IApiGameSettings = {
                        ...gamePayload,
                        games: {
                            puzzle: { ...gamePayload.games.puzzle, enabled: originalKioskNative?.games?.permission.puzzle ?? false },
                            quiz: { ...gamePayload.games.quiz, enabled: originalKioskNative?.games?.permission.quiz ?? false },
                            "flip-card": {
                                ...gamePayload.games["flip-card"],
                                enabled: originalKioskNative?.games?.permission["flip-card"] ?? false,
                            },
                        },
                        prize: {
                            ...gamePayload.prize,
                            common: {
                                ...gamePayload.prize.common,
                                enabled: originalKioskNative?.games?.permission.prize ?? false,
                            },
                        },
                    };

                    await updateGameSetting({
                        character_id: characterId,
                        payload: rollbackPayload,
                    }).unwrap();

                    customToast.error(t("COMMON.SETTINGS_PARTIAL_ERROR") || "部分設定儲存失敗，已回復原狀態");
                    throw kioskError;
                }

                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");

                reset(dataWithUrls as GameFormWithFiles, {
                    keepValues: true,
                    keepDirty: false,
                    keepErrors: false,
                });
            } catch (error) {
                if (error instanceof Error && error.message.includes("Failed to upload")) {
                    throw error;
                }
                if (!(error instanceof Error && error.message.includes("部分設定"))) {
                    customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
                }
                throw error;
            }
        },
        [
            currentKioskNative,
            uploadAllFiles,
            convertFormToGamePayload,
            convertFormToGamesToggle,
            updateGameSetting,
            characterId,
            updateKioskSetting,
            t,
            reset,
        ]
    );

    const saveHandler = useCallback(async () => {
        await handleSubmit(onSubmit as never)();
    }, [handleSubmit, onSubmit]);

    // ============ Tab Guard ============
    useTabGuard("game", methods.formState.isDirty, saveHandler);

    // ============ Initialize Form from API ============
    useEffect(() => {
        // 等待兩個 API 都載入完成
        if (isLoadingGameSetting || isLoadingKioskSetting) return;

        if (!isInitialLoadRef.current) return;

        const gameSetting = gameSettingResponse?.data?.game_setting ?? null;
        const gamesToggle = kioskSettingResponse?.data?.native?.games ?? INITIAL_GAMES_TOGGLE;

        const formData = convertApiToFormData(gameSetting, gamesToggle);
        reset(formData as GameFormWithFiles);

        isInitialLoadRef.current = false;
    }, [
        gameSettingResponse?.data?.game_setting,
        kioskSettingResponse?.data?.native?.games,
        isLoadingGameSetting,
        isLoadingKioskSetting,
        convertApiToFormData,
        reset,
    ]);

    // ============ Return Values ============
    return {
        // Form - return entire methods for proper formState reactivity
        methods,
        handleSubmit,
        onSubmit,
        watch,
        setValue,

        // Language
        selectedLang: selectedLanguage,
        setSelectedLang: setSelectedLanguage,
        getItemCountsByLang,

        // Toggle States
        isEnabled: watchedEnabled,
        isPuzzleEnabled: watchedPuzzleEnabled,
        isQuizEnabled: watchedQuizEnabled,
        isFlipCardEnabled: watchedFlipCardEnabled,
        isPrizeEnabled: watchedPrizeEnabled,

        // Toggle Handlers
        handleMasterToggle,
        handleGameToggle,

        // Item Handlers
        handleAddItem,
        handleRemoveItem,

        // Loading States
        isLoadingGameSetting: isLoading, // 兩個 API 都完成才算載入完成
        isUpdatingGameSetting: isUpdating,
        isUploading,
        isSubmitting: isUpdating,
    };
};

export default useGameSettings;
